import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Badge, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

type Window = "24h" | "7d" | "30d" | "all";

interface TransparencyData {
  operatorId: string;
  operatorName: string;
  window: Window;
  theoreticalRtp: number;
  actualRtp: number;
  rounds: number;
  wagered: number;
  paid: number;
  biggestMultiplier: number;
  biggestPayout: number;
  windowFrom: number;
}

const WINDOW_LABEL: Record<Window, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  all: "All time",
};

/**
 * Sample-size sanity threshold. Below this we mark the actual-RTP figure
 * "low confidence" because crash variance can wobble RTP by several percent
 * in the first thousand rounds. The real Wilson-interval treatment lives in
 * the explainer page; this is a coarse safety rail in the headline UI.
 */
const MIN_SAMPLE_HIGH_CONFIDENCE = 1_000;

export function Transparency() {
  const { operatorId } = useParams<{ operatorId: string }>();
  const [win, setWin] = useState<Window>("30d");
  const [data, setData] = useState<TransparencyData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!operatorId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetch(`/public/transparency/${encodeURIComponent(operatorId)}?window=${win}`)
      .then(async (r) => {
        if (r.status === 404) throw new Error("Transparency is not enabled for this operator.");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: TransparencyData) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [operatorId, win]);

  return (
    <>
      <Seo
        title={`Transparency · ${operatorId ?? ""} · Vault Breaker Studios`}
        description="Live RTP and gameplay statistics, published publicly with operator consent."
        path={`/transparency/${operatorId ?? ""}`}
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          PROVABLY FAIR · LIVE STATS
        </Badge>
        <SectionHeading
          eyebrow={`Operator · ${operatorId ?? ""}`}
          title={data?.operatorName ?? "Operator transparency"}
          lead="Live aggregate from the server-side audit log. Theoretical RTP comes from the engine math; actual RTP is empirical and will drift around the theoretical value with sample-size variance."
        />

        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {(Object.keys(WINDOW_LABEL) as Window[]).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWin(w)}
              style={{
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: w === win ? "var(--cyan)" : "var(--bg-2)",
                color: w === win ? "#0A0E14" : "var(--fg-2)",
                border: `1px solid ${w === win ? "var(--cyan)" : "var(--line)"}`,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {WINDOW_LABEL[w]}
            </button>
          ))}
        </div>

        {loading ? (
          <Card style={{ padding: 32, textAlign: "center", color: "var(--fg-3)" }}>Loading…</Card>
        ) : err ? (
          <Card
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--red)",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
            }}
          >
            {err}
          </Card>
        ) : data ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 14,
              }}
            >
              <Stat label="Theoretical RTP" value={`${(data.theoreticalRtp * 100).toFixed(2)}%`} />
              <Stat
                label={`Actual RTP (${WINDOW_LABEL[data.window]})`}
                value={data.rounds > 0 ? `${(data.actualRtp * 100).toFixed(2)}%` : "—"}
                subtext={
                  data.rounds < MIN_SAMPLE_HIGH_CONFIDENCE
                    ? `Low confidence · ${data.rounds} rounds`
                    : `${data.rounds.toLocaleString()} rounds`
                }
                tone={
                  data.rounds === 0
                    ? "muted"
                    : Math.abs(data.actualRtp - data.theoreticalRtp) < 0.02
                      ? "good"
                      : "warn"
                }
              />
              <Stat label="Total wagered" value={data.wagered.toFixed(5)} />
              <Stat label="Total paid out" value={data.paid.toFixed(5)} />
              <Stat
                label="Biggest cashout"
                value={data.biggestMultiplier > 0 ? `${data.biggestMultiplier.toFixed(2)}×` : "—"}
                subtext={data.biggestPayout > 0 ? `+${data.biggestPayout.toFixed(5)}` : undefined}
              />
              <Stat label="Rounds" value={data.rounds.toLocaleString()} />
            </div>

            <p
              style={{
                marginTop: 22,
                fontSize: 12,
                color: "var(--fg-3)",
                lineHeight: 1.6,
                maxWidth: 720,
              }}
            >
              Crash games are negative-EV by design. The 1% house edge on every round means actual
              RTP converges toward the theoretical value as the sample size grows. Below ~1,000
              rounds the empirical figure carries large uncertainty — variance, not "rigging," is
              the explanation when actual deviates from theoretical. This page exists so the
              operator's public commitment to fair math is verifiable, not aspirational.
            </p>
          </>
        ) : null}
      </Section>
    </>
  );
}

function Stat({
  label,
  value,
  subtext,
  tone,
}: {
  label: string;
  value: string;
  subtext?: string;
  tone?: "good" | "warn" | "muted";
}) {
  const valueColor =
    tone === "good"
      ? "var(--green)"
      : tone === "warn"
        ? "var(--gold-bright)"
        : tone === "muted"
          ? "var(--fg-3)"
          : "var(--fg-1)";
  return (
    <Card style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        className="label"
        style={{
          fontSize: 9,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {label}
      </span>
      <span
        className="mono"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 24,
          fontWeight: 700,
          color: valueColor,
          letterSpacing: "0.02em",
        }}
      >
        {value}
      </span>
      {subtext && (
        <span style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
          {subtext}
        </span>
      )}
    </Card>
  );
}
